<template>
  <button
    v-if="isVoiceRecordingSupported"
    class="focus:outline-none"
    @click.prevent="handleSpeechRecognition"
  >
    <div
      v-if="isListening"
      class="recording-pulse-container"
      data-cy="pulse-button"
    >
      <div class="pulse-ring pulse-ring-1"></div>
      <div class="pulse-ring pulse-ring-2"></div>
      <div class="pulse-ring pulse-ring-3"></div>
      <div class="recording-center"></div>
    </div>
    <icon-base
      v-else
      data-cy="microphone-icon"
      class="text-label"
      :icon="IconMicrophone"
      v-tooltip="tooltip"
    />
  </button>
</template>
<script setup lang="ts">
import { computed, watch } from "vue";
import { useI18n } from "vue-i18n";

import { useSpeechRecognition } from "@/hooks/useSpeechRecognition";

import IconMicrophone from "@/components/icons/IconMicrophone.vue";
import IconBase from "@/components/ui/IconBase.vue";

const emit = defineEmits<{
  "text:updated": [string];
  "recording:finished": [];
}>();

const { t } = useI18n();
const { isSupported, isListening, result, start, stop } =
  useSpeechRecognition();

const tooltip = computed(() => {
  return isListening.value
    ? t("VOICE.STOP_RECORDING")
    : t("VOICE.USE_MICROPHONE");
});

const isVoiceRecordingSupported = computed(() => {
  if (!isSupported) {
    return false;
  }

  const ua = navigator.userAgent;

  const isArc = !!getComputedStyle(document.documentElement).getPropertyValue(
    "--arc-palette-title"
  );
  const isEdgeOnMac = ua.includes("Edg/") && ua.includes("Macintosh");

  return !(isArc || isEdgeOnMac);
});

const handleSpeechRecognition = () => {
  isListening.value ? stopRecording() : start();
};

const stopRecording = () => {
  emit("recording:finished");
  stop();
};

watch(result, (updatedResult) => {
  emit("text:updated", updatedResult);
});

defineExpose({
  stopRecording,
});
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.recording-pulse-container {
  @apply relative left-0-5 flex h-5 w-5 items-center justify-center;
}

.recording-center {
  @apply relative z-4 h-[10px] w-[10px] rounded-2 bg-emerald-mist;
}

.pulse-ring {
  @apply absolute rounded border-2 border-[#06d6a01a];
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.pulse-ring-1 {
  @apply h-3 w-3;
  animation-delay: 0s;
}

.pulse-ring-2 {
  @apply h-4 w-4;
  animation-delay: 0.4s;
}

.pulse-ring-3 {
  @apply h-5 w-5;
  animation-delay: 0.8s;
}

@keyframes pulse {
  0% {
    transform: scale(0.8);
    opacity: 1;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.7;
  }
  100% {
    transform: scale(1.6);
    opacity: 0;
  }
}
</style>
